import { useQuery } from '@apollo/client';
import { Download, OpenInNew } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  chipClasses,
  ClickAwayListener,
  Divider,
  Popper,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFileStorage } from '@procureai/uix';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Streamdown } from 'streamdown';

import { components, allowedTags } from '../ChatMessage/MessageRenderer/MessageRenderer.config.tsx';
import { I18nNamespace } from '~/common/types';

import { GET_DOCUMENT_CHUNK_BY_ID } from './QueryCitationChunk.gql.ts';

export interface CitationProps {
  // Citation chunk ID
  id: string;
}

/**
 * Styled component for the citation avatar displaying the citation number.
 * Uses CSS counters to automatically number citations.
 * @component
 * @returns {JSX.Element} A styled span element representing the citation avatar
 * @remarks
 * - The citation number is displayed using the CSS `:before` pseudo-element.
 * - The counter is incremented for each citation instance.
 * - Styled with primary theme colors and circular shape.
 * @important
 * To make the citation counter work, ensure that `counterReset: 'citation'` is set on the parent container's CSS.
 * @example
 * ```tsx
 * <Box sx={{ counterReset: 'citation' }}>
 *   <CitationAvatar />
 *   <CitationAvatar />
 * </Box>
 * ```
 */
const CitationAvatar = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  lineHeight: '20px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: 12,
  counterIncrement: 'citation',
  [`&.${chipClasses.avatar}`]: {
    width: 24,
    height: 24,
    minWidth: 24,
  },
  ':before': {
    content: 'counter(citation)',
    width: '100%',
    textAlign: 'center',
    display: 'block',
  },
}));

const CitationContent = styled(Box)(({ theme }) => ({
  maxHeight: 200,
  overflowY: 'auto',
  fontStyle: 'italic',
  paddingLeft: theme.spacing(2),
  position: 'relative',
  borderLeft: `4px solid ${theme.palette.grey[300]}`,
}));

const PopperContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  borderRadius: 1,
  maxWidth: 450,
  minWidth: 450,
  width: 450,
  [theme.breakpoints.down('lg')]: {
    maxWidth: 300,
    minWidth: 300,
    width: 300,
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: 200,
    minWidth: 200,
    width: 200,
  },
}));

const Arrow = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: '-10px',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',

    borderTop: `10px solid ${theme.palette.background.paper}`,
  },
}));

/**
 * Citation component that displays a clickable chip with a citation number and file name.
 * When clicked, it shows a popover with citation details and download options.
 *
 * @component
 * @param {CitationProps} props - The component props
 * @param {string} props.id - The unique identifier for the citation
 *
 * @returns {JSX.Element} A citation chip with popover containing file information and download button
 *
 * @remarks
 * - Uses a CSS counter to automatically number citations
 * - Displays a tooltip with the file name on hover
 * - Shows a popover with citation details when clicked
 * - Provides PDF download functionality
 * - Uses ClickAwayListener to close the popover when clicking outside
 *
 * @important
 * To make the citation counter work, ensure that `counterReset: 'citation'` is set on the parent container's CSS.
 *
 * @example
 * ```tsx
 * <Box sx={{ counterReset: 'citation' }}>
 *   <Citation id="citation-123" />
 *   <Citation id="citation-124" />
 * </Box>
 * ```
 *
 * @todo Implement GraphQL query to fetch citation data by ID
 * @todo Add loading and error state handling
 * @todo Replace mock data with real data from query
 * @todo Implement actual download functionality using onFileDownload
 */
export const Citation = ({ id }: CitationProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'Chat.citations' });
  const anchorRef = useRef(null);
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);

  const [open, setOpen] = useState(false);
  const { download } = useFileStorage();

  // hast-util-sanitize (used by streamdown via rehype-sanitize) rewrites
  // the VALUE of every `id`/`name` attribute as `user-content-<original>`
  // to prevent DOM clobbering — so `id="abc"` arrives here as `id="user-content-abc"`.
  // Strip the prefix before using it as a chunk UUID.
  // See https://github.com/rehypejs/rehype-sanitize (clobberPrefix / clobber).
  const chunkId = id.replace(/^user-content-/, '');
  const { data, loading, error } = useQuery(GET_DOCUMENT_CHUNK_BY_ID, { variables: { chunkId } });
  if (loading) {
    return <Chip avatar={<CitationAvatar />} label={t('loading')} />;
  }
  if (error || !data?.sevenbridges.documentChunk) {
    return null; // Fail silently for now
  }

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleDownloadPDF = () => {
    download({
      uploadId: data.sevenbridges.documentChunk.fileId,
      fileName: data.sevenbridges.documentChunk.title,
    });
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={anchorRef} component="span" sx={{ position: 'relative' }}>
        <Tooltip title={data.sevenbridges.documentChunk.title} placement="right" arrow>
          <Chip
            component="span" // avoid <div> cannot appear as a descendant of <p>
            avatar={<CitationAvatar />}
            label={data.sevenbridges.documentChunk.title}
            sx={{ maxWidth: 200 }}
            onClick={handleClick}
          />
        </Tooltip>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="top-start"
          disablePortal={true}
          modifiers={[
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: false,
            },
            {
              name: 'arrow',
              options: {
                background: 'black',
                element: arrowRef,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 34],
              },
            },
          ]}
        >
          <Arrow ref={setArrowRef} />
          <PopperContainer>
            <Stack spacing={1} sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle1">{data.sevenbridges.documentChunk.title}</Typography>
              <Divider />
              <CitationContent>
                <Streamdown mode="static" components={components} allowedTags={allowedTags}>
                  {data.sevenbridges.documentChunk.content}
                </Streamdown>
              </CitationContent>
            </Stack>
            <Divider />
            <Stack direction="row" spacing={1} sx={{ p: 2 }}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<Download />}
                onClick={() => handleDownloadPDF()}
              >
                {t('downloadPdf')}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={<OpenInNew />}
                disabled={!data.sevenbridges.documentChunk.documentUrl}
                component="a"
                href={data.sevenbridges.documentChunk.documentUrl ?? ''}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('openLink')}
              </Button>
            </Stack>
          </PopperContainer>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
